import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Download, Save, FileText, Edit3, Stamp } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { formatDZD } from '../utils/currency';
import { generateDocumentPDF, downloadDocumentPDF } from '../utils/pdfGenerator';
import { numberToFrenchWords } from '../utils/numberToWords';
import './DocumentPreview.css';

// Fonction pour formater un compte bancaire avec le préfixe en gras
const formatBankAccount = (account) => {
  if (!account) return 'N/A';
  const parts = account.split(' ');
  if (parts.length < 2) return account;
  
  // Trouver l'index du nom de la banque (CPA, BADR, BNA, etc.)
  const bankIndex = parts.findIndex(part => 
    ['CPA', 'BADR', 'BNA', 'BEA', 'BDL', 'CNEP', 'AGB'].includes(part)
  );
  
  if (bankIndex === -1) {
    // Si on ne trouve pas de nom de banque connu, on prend le dernier mot
    const bankPrefix = parts[parts.length - 1];
    const accountNumber = parts.slice(0, -1).join(' ');
    return <>{accountNumber} <strong>{bankPrefix}</strong></>;
  }
  
  // Sinon on met en gras uniquement le nom de la banque
  const accountNumber = parts.slice(0, bankIndex).join(' ');
  const bankName = parts[bankIndex];
  const location = parts.slice(bankIndex + 1).join(' ');
  
  return <>{accountNumber} <strong>{bankName}</strong> {location}</>;
};

// Fonction pour mapper les modes de paiement vers leur libellé complet
const getPaymentMethodLabel = (method) => {
  const methodMap = {
    'virement': 'Virement bancaire',
    'cheque': 'Chèque',
    'especes': 'Espèces',
    'carte': 'Carte bancaire',
    'effet': 'Effet de commerce',
    'traite': 'Traite',
    'compensation': 'Compensation',
    'prélèvement': 'Prélèvement automatique',
    'mixte': 'Plusieurs modes',
    'crédit_documentaire': 'Crédit documentaire',
    'crédit_compte': 'Crédit sur compte client',
    'acompte': 'Acompte + solde',
    'echeances': 'Paiement échelonné',
    'livraison': 'À la livraison'
  };
  return methodMap[method] || method;
};

const DocumentPreview = ({ documentType, formData, clientData, onBack, onSave, isRealTimePreview = false }) => {
  const { settings } = useSettings();
  const [isSignatureMode, setIsSignatureMode] = useState(false);
  const [isStampMode, setIsStampMode] = useState(false);
  const [stampPosition, setStampPosition] = useState({ x: 0, y: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastX, setLastX] = useState(0);
  const [lastY, setLastY] = useState(0);
  const canvasRef = useRef(null);
  const documentRef = useRef(null);
  const stampRef = useRef(null);

  // Initialiser le canvas avec une résolution plus élevée
  useEffect(() => {
    if (canvasRef.current && documentRef.current && isSignatureMode) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Augmenter la résolution du canvas (3x)
      const scale = window.devicePixelRatio * 3;
      const rect = documentRef.current.getBoundingClientRect();
      
      canvas.width = rect.width * scale;
      canvas.height = rect.height * scale;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      
      // Configurer le contexte pour une meilleure qualité
      ctx.scale(scale, scale);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
    }
  }, [isSignatureMode]);

  const drawLine = (ctx, x1, y1, x2, y2) => {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  };

  const documentTitles = {
    'invoice': 'FACTURE',
    'proforma': 'FACTURE PROFORMA',
    'quote-request': 'DEMANDE DE DEVIS',
    'quote': 'DEVIS',
    'purchase-order': 'BON DE COMMANDE',
    'attachment': 'ATTACHEMENT',
    'delivery-note': 'BON DE LIVRAISON',
    'shipping-slip': 'BORDEREAU D\'ENVOI',
    'reception-note': 'BON DE RÉCEPTION',
    'return-slip': 'BORDEREAU DE RETOUR',
    'stock-sheet': 'FICHE DE STOCK',
    'material-exit': 'BON DE SORTIE MATÉRIEL'
  };

  const getProductName = (item) => {
    if (!item) return '';
    
    if (typeof item === 'object') {
      return item.designation || item.reference || item.name || '';
    }
    
    return item.toString();
  };

  const generateItemCode = (item, index) => {
    // If item already has a code, use it
    if (item.itemCode || item.reference || item.sku) {
      return item.itemCode || item.reference || item.sku;
    }
    
    // Default categories with prefixes (fallback)
    const defaultCategories = [
      { name: 'Services Informatiques', prefix: 'SRV' },
      { name: 'Matériel Informatique', prefix: 'MAT' },
      { name: 'Formation', prefix: 'FOR' },
      { name: 'Consulting', prefix: 'CST' },
      { name: 'Maintenance', prefix: 'MNT' }
    ];
    
    // Try to find matching category prefix
    let prefix = 'GEN'; // Default generic prefix
    
    if (item.category) {
      const matchedCategory = defaultCategories.find(cat => 
        cat.name.toLowerCase() === item.category.toLowerCase() ||
        item.category.toLowerCase().includes(cat.name.toLowerCase())
      );
      
      if (matchedCategory) {
        prefix = matchedCategory.prefix;
      }
    }
    
    // If no category match, use basic logic
    if (prefix === 'GEN') {
      const isService = item.type === 'service' || 
                       (item.category && item.category.toLowerCase().includes('service')) ||
                       (item.name && item.name.toLowerCase().includes('service'));
      prefix = isService ? 'SRV' : 'MAT';
    }
    
    const number = String(index + 1).padStart(2, '0');
    return `${prefix}-${number}`;
  };

  // Function to generate client/supplier code based on data
  const generatePartnerCode = (data, isSupplier = false) => {
    if (!data) return 'N/A';
    
    if (isSupplier) {
      const number = String(data.id).padStart(2, '0');
      return `FRN-${number}`;
    }
    
    // For clients
    if (data.clientNumber) {
      const number = String(data.clientNumber).padStart(2, '0');
      return `CLI-${number}`;
    }
    
    // Fallback: use ID if no clientNumber
    const number = String(data.id).padStart(2, '0');
    return `CLI-${number}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleGeneratePDF = async () => {
    try {
      await generateDocumentPDF(documentType, formData, clientData, settings);
      alert('PDF généré avec succès !');
    } catch (error) {
      console.error('Erreur lors de la génération PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      await downloadDocumentPDF(documentType, formData, clientData, settings);
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const handleStartSigning = () => {
    setIsSignatureMode(true);
    // Réinitialiser le canvas si nécessaire
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleStopSigning = () => {
    setIsSignatureMode(false);
  };

  const handleMouseDown = (e) => {
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setLastX(x);
    setLastY(y);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;
    
    const ctx = canvasRef.current.getContext('2d');
    drawLine(ctx, lastX, lastY, currentX, currentY);
    
    setLastX(currentX);
    setLastY(currentY);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    setLastX(x);
    setLastY(y);
    setIsDrawing(true);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = touch.clientX - rect.left;
    const currentY = touch.clientY - rect.top;
    
    const ctx = canvasRef.current.getContext('2d');
    drawLine(ctx, lastX, lastY, currentX, currentY);
    
    setLastX(currentX);
    setLastY(currentY);
  };

  const handleTouchEnd = () => {
    setIsDrawing(false);
  };

  const handleStampClick = () => {
    setIsStampMode(!isStampMode);
    setIsSignatureMode(false);
  };

  const handleDocumentClick = (e) => {
    if (!isStampMode) return;
    
    const rect = documentRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setStampPosition({ x, y });
  };

  const renderStamp = () => {
    const today = new Date().toLocaleDateString('fr-FR');
    return (
      <div className="stamp-preview" style={{
        position: 'absolute',
        left: `${stampPosition.x}px`,
        top: `${stampPosition.y}px`,
        transform: 'translate(-50%, -50%)',
        zIndex: 150
      }} ref={stampRef}>
        <div className="stamp-outer-circle">
          <div className="stamp-inner-circle" />
          <div className="stamp-inner-content">
            <div className="stamp-top-text">• Certifié conforme •</div>
            <div className="stamp-company-name">{settings.company.name}</div>
            <div className="stamp-details">
              <div className="stamp-rc">RC N° {settings.company.rc}</div>
              <div className="stamp-nif">NIF {settings.company.nif}</div>
              <div className="stamp-date">{today}</div>
            </div>
            <div className="stamp-bottom-text">• Document officiel •</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`document-preview ${isSignatureMode ? 'signing' : ''} ${isStampMode ? 'stamping' : ''}`}
         data-document-type={documentType}>
      {/* Action Bar - Increased z-index */}
      {!isRealTimePreview && (
        <div className="preview-actions" style={{ position: 'relative', zIndex: 200 }}>
          <button className="btn btn-secondary" onClick={onBack}>
            <ArrowLeft size={16} />
            Retour à l'édition
          </button>
          
          <div className="action-buttons">
            <button className="btn btn-success" onClick={handleDownloadPDF}>
              <Download size={16} />
              Télécharger PDF
            </button>
            <button className="btn btn-primary" onClick={onSave}>
              <Save size={16} />
              Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Document Preview */}
      <div 
        className="document-container" 
        ref={documentRef}
        onClick={handleDocumentClick}
        style={{ cursor: isStampMode ? 'crosshair' : 'default' }}
      >
        <div className="document-paper">
          {/* Header with new design */}
          <div className="new-document-header">
            <div className="header-content">
              <div className="left-section">
                <div className="company-logo-new">
                  <img src={settings.company.logo} alt={`${settings.company.name} Logo`} className="logo-image" />
                </div>
              </div>
              
              <div className="center-section">
                <div className="company-name-french">{settings.company.name}</div>
                <div className="company-name-arabic">{settings.company.nameArabic}</div>
                <div className="company-activity">{settings.company.activity}</div>
                <div className="company-services-italic">{settings.company.services}</div>
            
                <div className="small-separator-line"></div>
            
                <div className="company-details-inline">
                  <div className="details-line">
                    <span><strong>ARTICLE N° :</strong> {settings.company.articleNumber} - <strong>NIF N° :</strong> {settings.company.nif} - <strong>NIS N° :</strong> {settings.company.nis} - <strong>RC N° :</strong> {settings.company.rc}</span>
                  </div>
                  <div className="details-line">
                    <span><strong>COMPTES BANCAIRE N° :</strong> {[
                  settings.company.bankAccount1,
                  settings.company.bankAccount2,
                  settings.company.bankAccount3
                ].filter(account => account && account.trim() !== '').map(account => formatBankAccount(account)).reduce((prev, curr, index) => 
                  index === 0 ? [curr] : [...prev, ' | ', curr], [])}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="blue-separator-line"></div>
          </div>
          
          {/* Document Title Section */}
          <div className="document-title-section">
            <h2 className="document-title-main">{documentTitles[documentType]}</h2>
          </div>

          {/* Document Information Section */}
          <div className="document-info-grid">
            <div className="document-info-left">
              <div className="info-row">
                <div className="info-label">{(() => {
                  const labelMap = {
                    'invoice': 'Facture N°:',
                    'proforma': 'PROFORMA N°:',
                    'quote-request': 'Demande devis N°:',
                    'quote': 'Devis N°:',
                    'purchase-order': 'Bon commande N°:',
                    'attachment': 'Attachement N°:'
                  };
                  return labelMap[documentType] || 'Document N°:';
                })()}</div>
                <div className="info-value">{formData.number}</div>
              </div>
              <div className="info-row">
                <div className="info-label">{`${settings?.company?.city || 'Alger'} le:`}</div>
                <div className="info-value">{formData.date}</div>
              </div>
              {documentType === 'invoice' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Paiement:</div>
                    <div className="info-value">{getPaymentMethodLabel(formData.paymentMethod)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Client N°:</div>
                    <div className="info-value">{generatePartnerCode(clientData, false)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Échéance:</div>
                    <div className="info-value">{formData.dueDate}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Ref:</div>
                    <div className="info-value">{(() => {
                      const categories = formData.items?.map(item => item.category).filter(Boolean);
                      const uniqueCategories = [...new Set(categories)];
                      
                      if (uniqueCategories.length === 0) {
                        return 'N/A';
                      } else if (uniqueCategories.length === 1) {
                        // Une seule catégorie : afficher le nom complet
                        return uniqueCategories[0];
                      } else {
                        // Plusieurs catégories : utiliser les préfixes
                        const categoryPrefixes = {
                          'Services Informatiques': 'SRV',
                          'Matériel Informatique': 'MAT',
                          'Formation': 'FOR',
                          'Consulting': 'CST',
                          'Maintenance': 'MNT'
                        };
                        
                        const prefixes = uniqueCategories.map(category => {
                          return categoryPrefixes[category] || category.substring(0, 3).toUpperCase();
                        });
                        
                        return prefixes.join('/');
                      }
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Établie par:</div>
                    <div className="info-value">{formData.issuedBy}</div>
                  </div>
                    <div className="info-label">Commercial:</div>
                    <div className="info-value">{formData.salesPerson}</div>
                </>
              )}
              {documentType === 'proforma' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Valable jusqu'au:</div>
                    <div className="info-value">{formData.validUntil}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Délai livraison:</div>
                    <div className="info-value">{formData.estimatedDelivery}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Paiement:</div>
                    <div className="info-value">{getPaymentMethodLabel(formData.paymentMethod)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Client N°:</div>
                    <div className="info-value">{generatePartnerCode(clientData, false)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Établie par:</div>
                    <div className="info-value">{formData.issuedBy}</div>
                  </div>
                </>
              )}
              {documentType === 'quote-request' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Type de demande:</div>
                    <div className="info-value">{formData.requestType}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Réponse souhaitée avant le:</div>
                    <div className="info-value">{formData.responseDate}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Date livraison souhaitée:</div>
                    <div className="info-value">{formData.desiredStartDate}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Lieu de livraison:</div>
                    <div className="info-value">{formData.deliveryLocation}</div>
                  </div>
                </>
              )}
              {documentType === 'quote' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Client N°:</div>
                    <div className="info-value">{generatePartnerCode(clientData, false)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Valable jusqu'au:</div>
                    <div className="info-value">{formData.validUntil}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Délai de réalisation:</div>
                    <div className="info-value">{formData.deliveryTime || '15 jours ouvrables'}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Conditions paiement:</div>
                    <div className="info-value">{formData.paymentTerms === 'acompte' ? '50% à la commande, solde à la livraison' : `${formData.paymentTerms} jours`}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Type de projet:</div>
                    <div className="info-value">{formData.projectType}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Garantie:</div>
                    <div className="info-value">{formData.warranty}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Commercial:</div>
                    <div className="info-value">{formData.salesPerson}</div>
                  </div>
                  {formData.paymentConditions && (
                    <div className="info-row">
                      <div className="info-label">Conditions de paiement:</div>
                      <div className="info-value">{formData.paymentConditions}</div>
                    </div>
                  )}
                  {formData.conditions && (
                    <div className="info-row problem-description">
                      <div className="info-label">Conditions particulières:</div>
                      <div className="info-value">{formData.conditions}</div>
                    </div>
                  )}
                </>
              )}
              {documentType === 'purchase-order' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Fournisseur N°:</div>
                    <div className="info-value">{generatePartnerCode(clientData, true)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Date livraison souhaitée:</div>
                    <div className="info-value">{formData.deliveryDate}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Lieu de livraison:</div>
                    <div className="info-value">{formData.deliveryAddress}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Conditions paiement:</div>
                    <div className="info-value">{formData.paymentTerms === 'acompte' ? '50% à la commande, solde à la livraison' : `${formData.paymentTerms} jours`}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Responsable:</div>
                    <div className="info-value">{formData.issuedBy}</div>
                  </div>
                  {formData.quoteReference && (
                    <div className="info-row">
                      <div className="info-label">Ref. devis:</div>
                      <div className="info-value">{formData.quoteReference}</div>
                    </div>
                  )}
                  {formData.approvedBy && (
                    <div className="info-row">
                      <div className="info-label">Approuvé par:</div>
                      <div className="info-value">{formData.approvedBy}</div>
                    </div>
                  )}
                  {formData.terms && (
                    <div className="info-row problem-description">
                      <div className="info-label">Conditions de commande:</div>
                      <div className="info-value">{formData.terms}</div>
                    </div>
                  )}
                </>
              )}
              {documentType === 'attachment' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Objet:</div>
                    <div className="info-value">{formData.attachment?.subject || formData.subject}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Type:</div>
                    <div className="info-value">{(() => {
                      const typeMap = {
                        'technique': 'Technique',
                        'fonctionnel': 'Fonctionnel',
                        'commercial': 'Commercial',
                        'juridique': 'Juridique',
                        'qualité': 'Qualité',
                        'sécurité': 'Sécurité',
                        'specification': 'Spécification technique',
                        'requirement': 'Cahier des charges',
                        'procedure': 'Procédure',
                        'manual': 'Manuel',
                        'contract': 'Document contractuel',
                        'certification': 'Certification'
                      };
                      return typeMap[formData.attachment?.type || formData.attachmentType] || (formData.attachment?.type || formData.attachmentType);
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Version:</div>
                    <div className="info-value">{formData.attachment?.version || formData.version}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Priorité:</div>
                    <div className="info-value">{formData.attachment?.priority || formData.priority}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Statut:</div>
                    <div className="info-value">{(() => {
                      const statusMap = {
                        'brouillon': 'Brouillon',
                        'draft': 'Brouillon',
                        'review': 'En révision',
                        'approved': 'Approuvé',
                        'final': 'Final'
                      };
                      return statusMap[formData.attachment?.status || formData.status] || (formData.attachment?.status || formData.status);
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Responsable:</div>
                    <div className="info-value">{formData.attachment?.responsable || formData.issuedBy}</div>
                  </div>
                  
                  {/* Validation information */}
                  {(formData.attachment?.requiresApproval || formData.approvalRequired) && (
                    <div className="info-row">
                      <div className="info-label">Approbation requise:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {(formData.attachment?.technicalValidation || formData.technicalValidation) && (
                    <div className="info-row">
                      <div className="info-label">Validation technique:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {(formData.attachment?.legalReview || formData.legalReview) && (
                    <div className="info-row">
                      <div className="info-label">Révision juridique:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                </>
              )}
              
              {/* LOGISTIQUE - Bon de livraison */}
              {documentType === 'delivery-note' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Date de livraison:</div>
                    <div className="info-value">{formData.delivery?.date}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Heure:</div>
                    <div className="info-value">{formData.delivery?.time}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Bon de commande:</div>
                    <div className="info-value">{formData.delivery?.orderReference}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Transporteur:</div>
                    <div className="info-value">{formData.delivery?.carrier}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Chauffeur:</div>
                    <div className="info-value">{formData.delivery?.driver}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Véhicule:</div>
                    <div className="info-value">{formData.delivery?.vehicleType} ({formData.delivery?.vehicleRegistration})</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Conditions:</div>
                    <div className="info-value">{(() => {
                      const conditionsMap = {
                        'franco': 'Franco (livré)',
                        'départ_usine': 'Départ usine',
                        'port_dû': 'Port dû',
                        'port_payé': 'Port payé'
                      };
                      return conditionsMap[formData.delivery?.conditions] || formData.delivery?.conditions;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Statut:</div>
                    <div className="info-value">{(() => {
                      const statusMap = {
                        'en_cours': 'En cours',
                        'livrée': 'Livrée',
                        'partiellement_livrée': 'Partiellement livrée',
                        'refusée': 'Refusée',
                        'en_attente': 'En attente'
                      };
                      return statusMap[formData.delivery?.status] || formData.delivery?.status;
                    })()}</div>
                  </div>
                </>
              )}

              {/* LOGISTIQUE - Bordereau d'envoi */}
              {documentType === 'shipping-slip' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Expéditeur:</div>
                    <div className="info-value">{formData.sender?.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Destinataire:</div>
                    <div className="info-value">{formData.recipient?.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Date d'envoi:</div>
                    <div className="info-value">{formData.shipment?.date}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Mode d'expédition:</div>
                    <div className="info-value">{formData.shipment?.method}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Numéro de suivi:</div>
                    <div className="info-value">{formData.shipment?.trackingNumber}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Poids total:</div>
                    <div className="info-value">{formData.shipment?.totalWeight} kg</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Nombre de colis:</div>
                    <div className="info-value">{formData.shipment?.packageCount}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Type de colis:</div>
                    <div className="info-value">{formData.shipment?.packageType}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Volume:</div>
                    <div className="info-value">{formData.shipment?.volume} m³</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Valeur déclarée:</div>
                    <div className="info-value">{formData.shipment?.declaredValue ? formatDZD(formData.shipment.declaredValue) : ''}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Transporteur:</div>
                    <div className="info-value">{formData.shipment?.carrier}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Priorité:</div>
                    <div className="info-value">{(() => {
                      const priorityMap = {
                        'normale': 'Normale',
                        'urgente': 'Urgente',
                        'express': 'Express',
                        'économique': 'Économique'
                      };
                      return priorityMap[formData.shipment?.priority] || formData.shipment?.priority;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Assurance:</div>
                    <div className="info-value">{(() => {
                      const insuranceMap = {
                        'aucune': 'Aucune',
                        'standard': 'Assurance standard',
                        'complète': 'Assurance complète',
                        'valeur_déclarée': 'Valeur déclarée'
                      };
                      return insuranceMap[formData.shipment?.insurance] || formData.shipment?.insurance;
                    })()}</div>
                  </div>
                  
                  {/* Boolean options */}
                  {formData.shipment?.signatureRequired && (
                    <div className="info-row">
                      <div className="info-label">Signature requise:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.shipment?.fragile && (
                    <div className="info-row">
                      <div className="info-label">Fragile:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.shipment?.returnable && (
                    <div className="info-row">
                      <div className="info-label">Retour possible:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  
                  {/* Instructions */}
                  {formData.shipment?.instructions && (
                    <div className="info-row problem-description">
                      <div className="info-label">Instructions spéciales:</div>
                      <div className="info-value">{formData.shipment.instructions}</div>
                    </div>
                  )}
                </>
              )}

              {/* LOGISTIQUE - Bon de réception */}
              {documentType === 'reception-note' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Fournisseur:</div>
                    <div className="info-value">{formData.supplier?.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Date de réception:</div>
                    <div className="info-value">{formData.reception?.date}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Heure:</div>
                    <div className="info-value">{formData.reception?.time}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Bon de commande:</div>
                    <div className="info-value">{formData.reception?.orderRef}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Bon de livraison:</div>
                    <div className="info-value">{formData.reception?.deliveryNoteRef}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Réceptionné par:</div>
                    <div className="info-value">{formData.reception?.receivedBy}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Contrôlé par:</div>
                    <div className="info-value">{formData.reception?.controlledBy}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">État général:</div>
                    <div className="info-value">{(() => {
                      const conditionMap = {
                        'excellent': 'Excellent',
                        'bon': 'Bon',
                        'moyen': 'Moyen',
                        'mauvais': 'Mauvais',
                        'inutilisable': 'Inutilisable'
                      };
                      return conditionMap[formData.reception?.overallCondition] || formData.reception?.overallCondition;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Conformité quantité:</div>
                    <div className="info-value">{(() => {
                      const complianceMap = {
                        'conforme': 'Conforme',
                        'partielle': 'Livraison partielle',
                        'excédent': 'Quantité excédentaire',
                        'non_conforme': 'Non conforme'
                      };
                      return complianceMap[formData.reception?.quantityCompliance] || formData.reception?.quantityCompliance;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Conformité qualité:</div>
                    <div className="info-value">{(() => {
                      const qualityMap = {
                        'conforme': 'Conforme',
                        'avec_réserves': 'Avec réserves',
                        'non_conforme': 'Non conforme',
                        'à_vérifier': 'À vérifier'
                      };
                      return qualityMap[formData.reception?.qualityCompliance] || formData.reception?.qualityCompliance;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Destination stock:</div>
                    <div className="info-value">{(() => {
                      const destinationMap = {
                        'entrepôt_principal': 'Entrepôt principal',
                        'stock_tampon': 'Stock tampon',
                        'quarantaine': 'Quarantaine',
                        'sav': 'SAV',
                        'destruction': 'Destruction'
                      };
                      return destinationMap[formData.reception?.stockDestination] || formData.reception?.stockDestination;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Statut de réception:</div>
                    <div className="info-value">{(() => {
                      const statusMap = {
                        'en_attente': 'En attente',
                        'acceptée': 'Acceptée',
                        'acceptée_avec_réserves': 'Acceptée avec réserves',
                        'refusée': 'Refusée',
                        'partiellement_acceptée': 'Partiellement acceptée'
                      };
                      return statusMap[formData.reception?.status] || formData.reception?.status;
                    })()}</div>
                  </div>
                  {formData.reception?.documentsComplete && (
                    <div className="info-row">
                      <div className="info-label">Documents complets:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.reception?.packagingIntact && (
                    <div className="info-row">
                      <div className="info-label">Emballage intact:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.reception?.urgentProcessing && (
                    <div className="info-row">
                      <div className="info-label">Traitement urgent:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.reception?.observations && (
                    <div className="info-row problem-description">
                      <div className="info-label">Observations:</div>
                      <div className="info-value">{formData.reception.observations}</div>
                    </div>
                  )}
                </>
              )}

              {/* LOGISTIQUE - Bordereau de retour */}
              {documentType === 'return-slip' && (
                <>
                  {formData.supplier?.name && (
                    <div className="info-row">
                      <div className="info-label">Fournisseur destinataire:</div>
                      <div className="info-value">{formData.supplier.name}</div>
                    </div>
                  )}
                  {formData.return?.date && (
                    <div className="info-row">
                      <div className="info-label">Date de retour:</div>
                      <div className="info-value">{formData.return.date}</div>
                    </div>
                  )}
                  {formData.return?.originalInvoiceRef && (
                    <div className="info-row">
                      <div className="info-label">Facture d'origine:</div>
                      <div className="info-value">{formData.return.originalInvoiceRef}</div>
                    </div>
                  )}
                  {formData.return?.originalDeliveryRef && (
                    <div className="info-row">
                      <div className="info-label">Livraison d'origine:</div>
                      <div className="info-value">{formData.return.originalDeliveryRef}</div>
                    </div>
                  )}
                  {formData.return?.reason && (
                    <div className="info-row">
                      <div className="info-label">Motif de retour:</div>
                      <div className="info-value">{(() => {
                        const reasonMap = {
                          'défectueux': 'Produit défectueux',
                          'non_conforme': 'Non conforme',
                          'erreur_livraison': 'Erreur de livraison',
                          'endommagé': 'Endommagé transport',
                          'fin_contrat': 'Fin de contrat',
                          'changement_besoin': 'Changement de besoin',
                          'garantie': 'Sous garantie'
                        };
                        return reasonMap[formData.return.reason] || formData.return.reason;
                      })()}</div>
                    </div>
                  )}
                  {formData.return?.type && (
                    <div className="info-row">
                      <div className="info-label">Type de retour:</div>
                      <div className="info-value">{(() => {
                        const typeMap = {
                          'échange': 'Échange',
                          'remboursement': 'Remboursement',
                          'réparation': 'Réparation',
                          'avoir': 'Avoir',
                          'destruction': 'Destruction'
                        };
                        return typeMap[formData.return.type] || formData.return.type;
                      })()}</div>
                    </div>
                  )}
                  {formData.return?.condition && (
                    <div className="info-row">
                      <div className="info-label">État des marchandises:</div>
                      <div className="info-value">{(() => {
                        const conditionMap = {
                          'emballage_original': 'Emballage d\'origine',
                          'bon_état': 'Bon état',
                          'état_moyen': 'État moyen',
                          'endommagé': 'Endommagé',
                          'inutilisable': 'Inutilisable'
                        };
                        return conditionMap[formData.return.condition] || formData.return.condition;
                      })()}</div>
                    </div>
                  )}
                  {formData.return?.carrier && (
                    <div className="info-row">
                      <div className="info-label">Transporteur:</div>
                      <div className="info-value">{formData.return.carrier}</div>
                    </div>
                  )}
                  {formData.return?.inspector && (
                    <div className="info-row">
                      <div className="info-label">Contrôlé par:</div>
                      <div className="info-value">{formData.return.inspector}</div>
                    </div>
                  )}
                  {formData.return?.cost && (
                    <div className="info-row">
                      <div className="info-label">Coût de retour (DZD):</div>
                      <div className="info-value">{formData.return.cost}</div>
                    </div>
                  )}
                  {formData.return?.transportCost && (
                    <div className="info-row">
                      <div className="info-label">Frais de transport (DZD):</div>
                      <div className="info-value">{formData.return.transportCost}</div>
                    </div>
                  )}
                  {formData.return?.qualityCheck && (
                    <div className="info-row">
                      <div className="info-label">Contrôle qualité effectué:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.return?.restockable && (
                    <div className="info-row">
                      <div className="info-label">Remise en stock possible:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.return?.creditNote && (
                    <div className="info-row">
                      <div className="info-label">Avoir à émettre:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.return?.status && (
                    <div className="info-row">
                      <div className="info-label">Statut:</div>
                      <div className="info-value">{(() => {
                        const statusMap = {
                          'en_cours': 'En cours',
                          'accepté': 'Accepté',
                          'refusé': 'Refusé',
                          'traité': 'Traité',
                          'remboursé': 'Remboursé'
                        };
                        return statusMap[formData.return.status] || formData.return.status;
                      })()}</div>
                    </div>
                  )}
                  {formData.return?.problemDescription && (
                    <div className="info-row problem-description">
                      <div className="info-label">Détail du problème:</div>
                      <div className="info-value">{formData.return.problemDescription}</div>
                    </div>
                  )}
                </>
              )}

              {/* LOGISTIQUE - Fiche de stock */}
              {documentType === 'stock-sheet' && (
                <>
                  <div className="info-row">
                    <div className="info-label">Entrepôt:</div>
                    <div className="info-value">{formData.warehouse?.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Emplacement:</div>
                    <div className="info-value">{formData.warehouse?.location}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Date d'inventaire:</div>
                    <div className="info-value">{formData.movement?.date}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Responsable stock:</div>
                    <div className="info-value">{formData.movement?.operator}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Type d'opération:</div>
                    <div className="info-value">{(() => {
                      const typeMap = {
                        'inventaire': 'Inventaire',
                        'entrée': 'Entrée',
                        'sortie': 'Sortie',
                        'transfert': 'Transfert',
                        'ajustement': 'Ajustement',
                        'retour': 'Retour'
                      };
                      return typeMap[formData.movement?.type] || formData.movement?.type;
                    })()}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Stock précédent:</div>
                    <div className="info-value">{formData.stock?.previous}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Quantité mouvement:</div>
                    <div className="info-value">{formData.movement?.quantity}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Nouveau stock:</div>
                    <div className="info-value">{formData.stock?.new}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Document référence:</div>
                    <div className="info-value">{formData.movement?.documentRef}</div>
                  </div>
                </>
              )}

              {/* LOGISTIQUE - Bon de sortie matériel */}
              {documentType === 'material-exit' && (
                <>
                  {/* Entrepôt et responsables */}
                  {formData.warehouse?.name && (
                    <div className="info-row">
                      <div className="info-label">Entrepôt/Magasin:</div>
                      <div className="info-value">{formData.warehouse.name}</div>
                    </div>
                  )}
                  {formData.warehouse?.location && (
                    <div className="info-row">
                      <div className="info-label">Emplacement:</div>
                      <div className="info-value">{formData.warehouse.location}</div>
                    </div>
                  )}
                  {formData.warehouse?.manager && (
                    <div className="info-row">
                      <div className="info-label">Responsable magasin:</div>
                      <div className="info-value">{formData.warehouse.manager}</div>
                    </div>
                  )}
                  {formData.exit?.date && (
                    <div className="info-row">
                      <div className="info-label">Date de sortie:</div>
                      <div className="info-value">{formData.exit.date}</div>
                    </div>
                  )}
                  
                  {/* Produit */}
                  {formData.product?.reference && (
                    <div className="info-row">
                      <div className="info-label">Référence produit:</div>
                      <div className="info-value">{formData.product.reference}</div>
                    </div>
                  )}
                  {formData.product?.name && (
                    <div className="info-row">
                      <div className="info-label">Désignation produit:</div>
                      <div className="info-value">{formData.product.name}</div>
                    </div>
                  )}
                  {formData.product?.category && (
                    <div className="info-row">
                      <div className="info-label">Catégorie produit:</div>
                      <div className="info-value">{(() => {
                        const categoryMap = {
                          'marchandise': 'Marchandise',
                          'matière_première': 'Matière première',
                          'produit_fini': 'Produit fini',
                          'composant': 'Composant',
                          'consommable': 'Consommable',
                          'outillage': 'Outillage'
                        };
                        return categoryMap[formData.product.category] || formData.product.category;
                      })()}</div>
                    </div>
                  )}
                  {formData.materialExit?.quantity && (
                    <div className="info-row">
                      <div className="info-label">Quantité sortie:</div>
                      <div className="info-value">{formData.materialExit.quantity}</div>
                    </div>
                  )}
                  {formData.materialExit?.unit && (
                    <div className="info-row">
                      <div className="info-label">Unité:</div>
                      <div className="info-value">{formData.materialExit.unit}</div>
                    </div>
                  )}
                  {formData.product?.batchNumber && (
                    <div className="info-row">
                      <div className="info-label">Numéro de lot:</div>
                      <div className="info-value">{formData.product.batchNumber}</div>
                    </div>
                  )}
                  {formData.product?.expirationDate && (
                    <div className="info-row">
                      <div className="info-label">Date d'expiration:</div>
                      <div className="info-value">{formData.product.expirationDate}</div>
                    </div>
                  )}
                  {formData.materialExit?.documentRef && (
                    <div className="info-row">
                      <div className="info-label">Document de référence:</div>
                      <div className="info-value">{formData.materialExit.documentRef}</div>
                    </div>
                  )}
                  {formData.materialExit?.operator && (
                    <div className="info-row">
                      <div className="info-label">Opérateur:</div>
                      <div className="info-value">{formData.materialExit.operator}</div>
                    </div>
                  )}
                  {formData.materialExit?.approvedBy && (
                    <div className="info-row">
                      <div className="info-label">Validé par:</div>
                      <div className="info-value">{formData.materialExit.approvedBy}</div>
                    </div>
                  )}
                  
                  {/* Demande */}
                  {formData.requester?.name && (
                    <div className="info-row">
                      <div className="info-label">Demandeur:</div>
                      <div className="info-value">{formData.requester.name}</div>
                    </div>
                  )}
                  {formData.requester?.department && (
                    <div className="info-row">
                      <div className="info-label">Service:</div>
                      <div className="info-value">{formData.requester.department}</div>
                    </div>
                  )}
                  {formData.requester?.position && (
                    <div className="info-row">
                      <div className="info-label">Fonction:</div>
                      <div className="info-value">{formData.requester.position}</div>
                    </div>
                  )}
                  {formData.exit?.warehouse && (
                    <div className="info-row">
                      <div className="info-label">Entrepôt de sortie:</div>
                      <div className="info-value">{formData.exit.warehouse}</div>
                    </div>
                  )}
                  {formData.exit?.destination && (
                    <div className="info-row">
                      <div className="info-label">Destination:</div>
                      <div className="info-value">{formData.exit.destination}</div>
                    </div>
                  )}
                  {formData.exit?.purpose && (
                    <div className="info-row">
                      <div className="info-label">Type de sortie:</div>
                      <div className="info-value">{(() => {
                        const typeMap = {
                          'utilisation': 'Utilisation',
                          'prêt': 'Prêt',
                          'maintenance': 'Maintenance',
                          'réparation': 'Réparation',
                          'test': 'Test/Essai',
                          'formation': 'Formation',
                          'démonstration': 'Démonstration'
                        };
                        return typeMap[formData.exit.purpose] || formData.exit.purpose;
                      })()}</div>
                    </div>
                  )}
                  {formData.exit?.project && (
                    <div className="info-row">
                      <div className="info-label">Projet:</div>
                      <div className="info-value">{formData.exit.project}</div>
                    </div>
                  )}
                  {formData.exit?.urgency && (
                    <div className="info-row">
                      <div className="info-label">Urgence:</div>
                      <div className="info-value">{(() => {
                        const urgencyMap = {
                          'normale': 'Normale',
                          'urgente': 'Urgente',
                          'très_urgente': 'Très urgente',
                          'programmée': 'Programmée'
                        };
                        return urgencyMap[formData.exit.urgency] || formData.exit.urgency;
                      })()}</div>
                    </div>
                  )}
                  {formData.exit?.approvedBy && (
                    <div className="info-row">
                      <div className="info-label">Autorisé par:</div>
                      <div className="info-value">{formData.exit.approvedBy}</div>
                    </div>
                  )}
                  {formData.exit?.warehouseKeeper && (
                    <div className="info-row">
                      <div className="info-label">Magasinier:</div>
                      <div className="info-value">{formData.exit.warehouseKeeper}</div>
                    </div>
                  )}
                  {formData.exit?.workOrder && (
                    <div className="info-row">
                      <div className="info-label">Ordre de travail:</div>
                      <div className="info-value">{formData.exit.workOrder}</div>
                    </div>
                  )}
                  {formData.exit?.costCenter && (
                    <div className="info-row">
                      <div className="info-label">Centre de coût:</div>
                      <div className="info-value">{formData.exit.costCenter}</div>
                    </div>
                  )}
                  {formData.exit?.expectedReturnDate && (
                    <div className="info-row">
                      <div className="info-label">Date de retour prévue:</div>
                      <div className="info-value">{formData.exit.expectedReturnDate}</div>
                    </div>
                  )}
                  {formData.exit?.status && (
                    <div className="info-row">
                      <div className="info-label">Statut de sortie:</div>
                      <div className="info-value">{(() => {
                        const statusMap = {
                          'en_cours': 'En cours',
                          'sortie': 'Sortie',
                          'retournée': 'Retournée',
                          'perdue': 'Perdue',
                          'consommée': 'Consommée'
                        };
                        return statusMap[formData.exit.status] || formData.exit.status;
                      })()}</div>
                    </div>
                  )}
                  
                  {/* Conditionally show boolean fields only if true */}
                  {formData.exit?.returnExpected && (
                    <div className="info-row">
                      <div className="info-label">Retour attendu:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.exit?.conditionCheck && (
                    <div className="info-row">
                      <div className="info-label">Vérification état effectuée:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  {formData.exit?.insuranceRequired && (
                    <div className="info-row">
                      <div className="info-label">Assurance requise:</div>
                      <div className="info-value">Oui</div>
                    </div>
                  )}
                  
                  {formData.exit?.justification && (
                    <div className="info-row problem-description">
                      <div className="info-label">Justification de la demande:</div>
                      <div className="info-value">{formData.exit.justification}</div>
                    </div>
                  )}
                  
                  {formData.materialExit?.observations && (
                    <div className="info-row problem-description">
                      <div className="info-label">Observations:</div>
                      <div className="info-value">{formData.materialExit.observations}</div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="document-info-right">
              <div className="recipient-box">
                <div className="recipient-content">
                  <div className="recipient-name">
                    <span className="doit-label">
                      {documentType === 'quote-request' ? 'Fournisseur: ' : 
                       documentType === 'proforma' ? 'Client: ' :
                       documentType === 'purchase-order' ? 'Fournisseur: ' :
                       documentType === 'attachment' ? 'Destinataire: ' : 
                       documentType === 'delivery-note' ? 'Destinataire: ' :
                       documentType === 'shipping-slip' ? 'Destinataire: ' :
                       documentType === 'reception-note' ? 'Fournisseur: ' :
                       documentType === 'return-slip' ? 'Client: ' :
                       documentType === 'stock-sheet' ? 'Responsable: ' :
                       documentType === 'material-exit' ? 'Destination: ' : 'Client: '}
                    </span>
                    {documentType === 'stock-sheet' ? formData.movement?.operator : 
                     documentType === 'material-exit' ? formData.exit?.destination :
                     clientData?.name}
                  </div>
                  <div className="recipient-address">
                    {documentType === 'stock-sheet' ? formData.stock?.warehouse + (formData.stock?.location ? ', ' + formData.stock?.location : '') : 
                     documentType === 'material-exit' ? '' :
                     clientData?.address + (clientData?.city ? ', ' + clientData?.city : '')}
                  </div>
                  <div className="recipient-details">
                    {/* Only show fiscal details for clients/suppliers, not for logistique documents that don't need them */}
                    {!['stock-sheet', 'material-exit'].includes(documentType) && (
                      <>
                    <div className="detail-row">
                      <span className="detail-label">ID Fiscal:</span>
                      <span className="detail-value">{clientData?.fiscalId}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Article:</span>
                      <span className="detail-value">{clientData?.article}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">RC N°:</span>
                      <span className="detail-value">{clientData?.rc}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Contact Tel:</span>
                      <span className="detail-value">{clientData?.phone}</span>
                    </div>
                      </>
                    )}
                    {documentType === 'quote-request' && clientData?.specialties && (
                      <div className="detail-row">
                        <span className="detail-label">Spécialités:</span>
                        <span className="detail-value">{clientData.specialties.join(', ')}</span>
                      </div>
                    )}
                    {documentType === 'stock-sheet' && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">Opération:</span>
                          <span className="detail-value">{(() => {
                            const typeMap = {
                              'inventaire': 'Inventaire',
                              'entrée': 'Entrée',
                              'sortie': 'Sortie',
                              'transfert': 'Transfert',
                              'ajustement': 'Ajustement'
                            };
                            return typeMap[formData.stock?.operationType] || formData.stock?.operationType;
                          })()}</span>
                  </div>
                        <div className="detail-row">
                          <span className="detail-label">Date:</span>
                          <span className="detail-value">{formData.stock?.inventoryDate}</span>
                </div>
                      </>
                    )}
                    {documentType === 'material-exit' && (
                      <>
                        <div className="detail-row">
                          <span className="detail-label">Type de sortie:</span>
                          <span className="detail-value">{(() => {
                            const typeMap = {
                              'utilisation': 'Utilisation',
                              'prêt': 'Prêt',
                              'maintenance': 'Maintenance',
                              'réparation': 'Réparation',
                              'test': 'Test/Essai',
                              'formation': 'Formation',
                              'démonstration': 'Démonstration'
                            };
                            return typeMap[formData.exit?.purpose] || formData.exit?.purpose;
                          })()}</span>
              </div>
                        <div className="detail-row">
                          <span className="detail-label">Projet:</span>
                          <span className="detail-value">{formData.exit?.project}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed information for attachment documents */}
          {documentType === 'attachment' && (
            <div className="attachment-details-section">
              <div className="attachment-info-grid">
                <div className="attachment-info-column">
                  {formData.subject && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Objet:</span>
                      <span className="attachment-info-value">{formData.subject}</span>
                    </div>
                  )}
                  {formData.attachmentType && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Type:</span>
                      <span className="attachment-info-value">
                        {(() => {
                          const typeMap = {
                            'specification': 'Spécification technique',
                            'requirement': 'Cahier des charges',
                            'procedure': 'Procédure',
                            'manual': 'Manuel',
                            'contract': 'Document contractuel',
                            'certification': 'Certification'
                          };
                          return typeMap[formData.attachmentType] || formData.attachmentType;
                        })()}
                      </span>
                    </div>
                  )}
                  {formData.priority && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Priorité:</span>
                      <span className="attachment-info-value">
                        {(() => {
                          const priorityMap = {
                            'normale': 'Normale',
                            'importante': 'Importante',
                            'urgente': 'Urgente'
                          };
                          return priorityMap[formData.priority] || formData.priority;
                        })()}
                      </span>
                    </div>
                  )}
                  {formData.status && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Statut:</span>
                      <span className="attachment-info-value">
                        {(() => {
                          const statusMap = {
                            'draft': 'Brouillon',
                            'review': 'En révision',
                            'approved': 'Approuvé',
                            'final': 'Final'
                          };
                          return statusMap[formData.status] || formData.status;
                        })()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="attachment-info-column">
                  {formData.version && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Version:</span>
                      <span className="attachment-info-value">{formData.version}</span>
                    </div>
                  )}
                  {formData.issuedBy && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Responsable:</span>
                      <span className="attachment-info-value">{formData.issuedBy}</span>
                    </div>
                  )}
                  {formData.attachment?.projectManager && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Chef de projet:</span>
                      <span className="attachment-info-value">{formData.attachment.projectManager}</span>
                    </div>
                  )}
                  {formData.attachment?.client && (
                    <div className="attachment-info-row">
                      <span className="attachment-info-label">Client/Donneur d'ordre:</span>
                      <span className="attachment-info-value">{formData.attachment.client}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Project timing information */}
              {(formData.attachment?.startDate || formData.attachment?.endDate || formData.attachment?.duration) && (
                <>
                  <h4 className="attachment-section-subtitle">Planning du projet</h4>
                  <div className="attachment-info-grid">
                    <div className="attachment-info-column">
                      {formData.attachment?.startDate && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Date de début:</span>
                          <span className="attachment-info-value">{formatDate(formData.attachment.startDate)}</span>
                        </div>
                      )}
                      {formData.attachment?.endDate && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Date de fin:</span>
                          <span className="attachment-info-value">{formatDate(formData.attachment.endDate)}</span>
                        </div>
                      )}
                    </div>
                    <div className="attachment-info-column">
                      {formData.attachment?.duration && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Durée estimée:</span>
                          <span className="attachment-info-value">{formData.attachment.duration}</span>
                        </div>
                      )}
                      {formData.attachment?.teamSize && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Taille de l'équipe:</span>
                          <span className="attachment-info-value">{formData.attachment.teamSize} personne(s)</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Technical information */}
              {(formData.attachment?.objectives || formData.attachment?.technicalConstraints || formData.attachment?.deliverables || formData.attachment?.acceptanceCriteria) && (
                <>
                  <h4 className="attachment-section-subtitle">Informations techniques</h4>
                  
                  {formData.attachment?.objectives && (
                    <div className="attachment-text-section">
                      <h5 className="attachment-text-title">Objectifs</h5>
                      <p className="attachment-text-content">{formData.attachment.objectives}</p>
                    </div>
                  )}
                  
                  {formData.attachment?.technicalConstraints && (
                    <div className="attachment-text-section">
                      <h5 className="attachment-text-title">Contraintes techniques</h5>
                      <p className="attachment-text-content">{formData.attachment.technicalConstraints}</p>
                    </div>
                  )}
                  
                  {formData.attachment?.deliverables && (
                    <div className="attachment-text-section">
                      <h5 className="attachment-text-title">Livrables attendus</h5>
                      <p className="attachment-text-content">{formData.attachment.deliverables}</p>
                    </div>
                  )}
                  
                  {formData.attachment?.acceptanceCriteria && (
                    <div className="attachment-text-section">
                      <h5 className="attachment-text-title">Critères d'acceptation</h5>
                      <p className="attachment-text-content">{formData.attachment.acceptanceCriteria}</p>
                    </div>
                  )}
                </>
              )}
              
              {/* Additional information */}
              {(formData.attachment?.standards || formData.attachment?.confidentiality || formData.attachment?.budget > 0) && (
                <>
                  <h4 className="attachment-section-subtitle">Informations additionnelles</h4>
                  
                  <div className="attachment-info-grid">
                    <div className="attachment-info-column">
                      {formData.attachment?.standards && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Standards et normes:</span>
                          <span className="attachment-info-value">{formData.attachment.standards}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="attachment-info-column">
                      {formData.attachment?.confidentiality && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Niveau de confidentialité:</span>
                          <span className="attachment-info-value">
                            {(() => {
                              const confidentialityMap = {
                                'public': 'Public',
                                'limited': 'Diffusion limitée',
                                'confidential': 'Confidentiel',
                                'secret': 'Secret'
                              };
                              return confidentialityMap[formData.attachment.confidentiality] || formData.attachment.confidentiality;
                            })()}
                          </span>
                        </div>
                      )}
                      
                      {formData.attachment?.budget > 0 && (
                        <div className="attachment-info-row">
                          <span className="attachment-info-label">Budget alloué:</span>
                          <span className="attachment-info-value">{formatDZD(formData.attachment.budget)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}



          {/* Items Table */}
          <div className="items-table-section">
            {(() => {
              // Calculate the longest item code length to determine CSS class
              const maxItemCodeLength = formData.items ? 
                Math.max(...formData.items.map((item, index) => 
                  generateItemCode(item, index).length
                )) : 0;
              
              // Check for wide numbers (2+ digits) in quantity and percentage
              const hasWideNumbers = formData.items ? 
                formData.items.some(item => {
                  const quantity = parseInt(item.quantity) || 1;
                  const tvaRate = parseFloat(formData.tvaRate) || 0;
                  return quantity >= 10 || tvaRate >= 10; // 2+ digits
                }) : false;
              
              // Apply CSS classes based on content
              let tableClass = 'items-table';
              
              // Special class for quote-request documents but maintain consistent layout
              if (documentType === 'quote-request') {
                tableClass += ' quote-request';
              }
              
              // Apply item code length classes for all documents for consistent sizing
              if (maxItemCodeLength >= 9) tableClass += ' very-long-item-codes'; // MAT-1000 (8 chars) and longer
              else if (maxItemCodeLength >= 7) tableClass += ' long-item-codes';  // MAT-100 (7 chars) and longer
              
              if (hasWideNumbers) tableClass += ' wide-numbers';
              
              return (
                <table className={tableClass}>
              <thead>
                <tr>
                  <th>N°</th>
                  {documentType !== 'quote-request' && <th>Item</th>}
                  <th>Désignation</th>
                  <th>U</th>
                  <th>Q</th>
                  <th>P.U HT</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {formData.items && formData.items.length > 0 ? (
                  formData.items.map((item, index) => {
                    const unitPrice = parseFloat(item.price || item.unitPrice) || 0;
                    const quantity = parseInt(item.quantity) || 1;
                    const total = item.total || (unitPrice * quantity);
                    const productName = getProductName(item);
                    
                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        {documentType !== 'quote-request' && <td>{generateItemCode(item, index)}</td>}
                        <td>{productName}</td>
                        <td>U</td>
                        <td>{quantity}</td>
                        <td>{formatDZD(unitPrice)}</td>
                        <td>{formatDZD(total)}</td>
                      </tr>
                    );
                  })
                ) : formData.specifications && formData.specifications.length > 0 ? (
                  formData.specifications.map((spec, index) => {
                    const budget = parseFloat(spec.budget) || 0;
                    const quantity = parseInt(spec.quantity) || 1;
                    const total = budget * quantity;
                    
                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        {documentType !== 'quote-request' && <td>{`SPEC-${String(index + 1).padStart(2, '0')}`}</td>}
                        <td>
                          {spec.description}
                          {spec.requirements && (
                            <div className="requirements-info">
                              <strong>Exigences:</strong> {spec.requirements}
                            </div>
                          )}
                        </td>
                        <td>U</td>
                        <td>{quantity}</td>
                        <td>{formatDZD(budget)}</td>
                        <td>{formatDZD(total)}</td>
                      </tr>
                    );
                  })
                ) : null}
                
                {/* Empty rows to fill the table */}
                {Array.from({ length: Math.max(0, 3 - (formData.items?.length || 0)) }, (_, index) => (
                  <tr key={`empty-${index}`}>
                    <td></td>
                    {documentType !== 'quote-request' && <td></td>}
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                ))}
              </tbody>
                </table>
              );
            })()}

            {/* Totals Section */}
            <div className="totals-section">
              <table className="totals-table">
                <tbody>
                  {(() => {
                    const items = formData.items || [];
                    
                    // Calculate subtotal from items or specifications
                    let subtotal = 0;
                    
                    // Handle specifications for quote-request documents
                    if (documentType === 'quote-request' && formData.specifications && formData.specifications.length > 0) {
                      subtotal = formData.specifications.reduce((sum, spec) => {
                        const budget = parseFloat(spec.budget) || 0;
                        const quantity = parseInt(spec.quantity) || 1;
                        return sum + (budget * quantity);
                      }, 0);
                    } else {
                      // Standard calculation for other document types
                      subtotal = items.reduce((sum, item) => {
                      if (item.total) {
                        return sum + parseFloat(item.total);
                      } else {
                        const unitPrice = parseFloat(item.price || item.unitPrice) || 0;
                        const quantity = parseInt(item.quantity) || 1;
                        return sum + (unitPrice * quantity);
                      }
                    }, 0);
                    }
                    
                    // Check toggle states for taxes based on document type
                    // Make sure TVA is shown for shipping-slip, quote-request and attachment when enabled
                    const showTva = formData.showTva !== false;
                    const showTap = formData.showTap !== false && documentType === 'proforma';
                    const showGuarantee = formData.showGuarantee && !['proforma'].includes(documentType);
                    const showStamp = formData.showStamp && !['proforma'].includes(documentType);

                    // For reception-note and return-slip, enforce TVA at 19% if not explicitly set
                    const tvaRate = showTva ? (parseFloat(formData.tvaRate) || 19) : 0;
                      
                    const tvaAmount = subtotal * (tvaRate / 100);
                    
                    // TAP calculation (only if enabled and for proforma)
                    const tapRate = showTap ? (parseFloat(formData.tapRate) || 2) : 0;
                    const tapAmount = subtotal * (tapRate / 100);
                    
                    const totalTTC = subtotal + tvaAmount + tapAmount;
                    const guarantee = showGuarantee ? (parseFloat(formData.guarantee) || 0) : 0;
                    const stamp = showStamp ? (parseFloat(formData.stamp) || 0) : 0;
                    
                    const netToPay = totalTTC - guarantee + stamp;
                    
                    return (
                      <>
                        <tr key="total-ht">
                          <td className="total-label">Total HT</td>
                          <td className="total-value">{formatDZD(subtotal)}</td>
                        </tr>
                        {showTva && (
                          <tr key="total-tva">
                            <td className="total-label">TVA ({tvaRate}%)</td>
                            <td className="total-value">{formatDZD(tvaAmount)}</td>
                          </tr>
                        )}
                        {showTap && (
                          <tr key="total-tap">
                            <td className="total-label">TAP ({tapRate}%)</td>
                            <td className="total-value">{formatDZD(tapAmount)}</td>
                          </tr>
                        )}
                        <tr key="total-ttc">
                          <td className="total-label">Total TTC</td>
                          <td className="total-value">{formatDZD(totalTTC)}</td>
                        </tr>
                        {showGuarantee && (
                          <tr key="guarantee">
                            <td className="total-label">Remise de garantie</td>
                            <td className="total-value">{formatDZD(guarantee)}</td>
                          </tr>
                        )}
                        {showStamp && (
                          <tr key="stamp">
                            <td className="total-label">Timbre</td>
                            <td className="total-value">{formatDZD(stamp)}</td>
                          </tr>
                        )}
                        <tr key="net-total" className="net-total">
                          <td className="total-label">Net à Payé</td>
                          <td className="total-value">{formatDZD(netToPay)}</td>
                        </tr>
                      </>
                    );
                  })()}
                </tbody>
              </table>
            
              {/* Amount in words - directly under the totals table */}
              <div className="amount-in-words">
              {(() => {
                const items = formData.items || [];
                  
                  // Calculate subtotal from items or specifications
                  let subtotal = 0;
                  
                  // Handle specifications for quote-request documents
                  if (documentType === 'quote-request' && formData.specifications && formData.specifications.length > 0) {
                    subtotal = formData.specifications.reduce((sum, spec) => {
                      const budget = parseFloat(spec.budget) || 0;
                      const quantity = parseInt(spec.quantity) || 1;
                      return sum + (budget * quantity);
                    }, 0);
                  } else {
                    // Standard calculation for other document types
                    subtotal = items.reduce((sum, item) => {
                  if (item.total) {
                    return sum + parseFloat(item.total);
                  } else {
                    const unitPrice = parseFloat(item.price || item.unitPrice) || 0;
                    const quantity = parseInt(item.quantity) || 1;
                    return sum + (unitPrice * quantity);
                  }
                }, 0);
                  }
                
                  const showTva = formData.showTva !== false;
                  const showTap = formData.showTap !== false && documentType === 'proforma';
                  const showGuarantee = formData.showGuarantee && !['proforma'].includes(documentType);
                  const showStamp = formData.showStamp && !['proforma'].includes(documentType);

                // For reception-note and return-slip, enforce TVA at 19% if not explicitly set
                const tvaRate = showTva ? (parseFloat(formData.tvaRate) || 19) : 0;
                  
                const tvaAmount = subtotal * (tvaRate / 100);
                
                const tapRate = showTap ? (parseFloat(formData.tapRate) || 2) : 0;
                const tapAmount = subtotal * (tapRate / 100);
                
                const totalTTC = subtotal + tvaAmount + tapAmount;
                const guarantee = showGuarantee ? (parseFloat(formData.guarantee) || 0) : 0;
                const stamp = showStamp ? (parseFloat(formData.stamp) || 0) : 0;
                
                const netToPay = totalTTC - guarantee + stamp;
                
                  return (
                  <p>
                    Arrêtée {(() => {
                                              const textMap = {
                          'invoice': 'La présente facture',
                          'proforma': 'La présente proforma',
                          'quote': 'Le présent devis',
                          'quote-request': 'La présente demande de devis',
                          'purchase-order': 'Le présent bon de commande',
                          'shipping-slip': 'Le présent bordereau',
                          'attachment': 'Le présent attachement'
                        };
                      return textMap[documentType] || 'Le présent document';
                    })()} à la somme de: <strong>{numberToFrenchWords(netToPay)}</strong>
                  </p>
                );
              })()}
              </div>
            </div>

            {/* Notes and Conditions Section */}
            <div className="notes-section">
              <h3 className="notes-title">
                {documentType === 'quote-request' ? 'EXIGENCES SPÉCIALES' :
                 documentType === 'quote' ? 'CONDITIONS COMMERCIALES' :
                 documentType === 'purchase-order' ? 'CONDITIONS DE COMMANDE' :
                 documentType === 'delivery-note' ? 'CONDITIONS DE LIVRAISON' :
                 documentType === 'shipping-slip' ? 'INSTRUCTIONS D\'EXPÉDITION' :
                 documentType === 'attachment' ? 'NOTES' : 'NOTES ET CONDITIONS'}
              </h3>
              <div className="notes-content">
                {documentType === 'quote' && formData.conditions ? <p>{formData.conditions}</p> : null}
                {documentType === 'purchase-order' && formData.terms ? <p>{formData.terms}</p> : null}
                {documentType === 'delivery-note' && formData.deliveryConditions ? <p>{formData.deliveryConditions}</p> : null}
                {documentType === 'shipping-slip' && formData.instructions ? <p>{formData.instructions}</p> : null}
                {documentType === 'quote-request' && formData.requirements ? <p>{formData.requirements}</p> : null}
                {documentType === 'proforma' && formData.conditions ? <p>{formData.conditions}</p> : null}
                {documentType === 'attachment' && formData.notes ? <p>{formData.notes}</p> : null}
                {(documentType === 'invoice' || !['quote', 'purchase-order', 'delivery-note', 'shipping-slip', 'quote-request', 'proforma', 'attachment'].includes(documentType)) ? <p>{formData.notes || ''}</p> : null}
              </div>
            </div>

            {/* Simple Footer */}
            <div className="simple-footer">
              <div className="footer-separator"></div>
              <div className="footer-content">
                <div className="footer-line">
                  <strong>Siège Social :</strong> {settings.company.address}, {settings.company.postalCode} {settings.company.city}, {settings.company.country}.
                </div>
                <div className="footer-line">
                  <strong>Tel / Fax :</strong> {settings.company.phone}
                </div>
                <div className="footer-line">
                  <strong>E-mail :</strong> <span className="email-text">{settings.company.email}</span>
                </div>
                <div className="footer-line">
                  <strong>Site Web:</strong> <span className="website-text">{settings.company.website}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stamp Element */}
        {isStampMode && renderStamp()}
      </div>

      {/* Signature Canvas Overlay - Adjusted position and size */}
      {isSignatureMode && (
        <div className="signature-overlay" style={{
          position: 'absolute',
          top: documentRef.current ? documentRef.current.offsetTop : 0,
          left: documentRef.current ? documentRef.current.offsetLeft : 0,
          width: documentRef.current ? documentRef.current.clientWidth : '100%',
          height: documentRef.current ? documentRef.current.clientHeight : '100%',
          zIndex: 100
        }}>
          <canvas
            ref={canvasRef}
            className="signature-canvas-overlay"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            width={documentRef.current?.clientWidth || 800}
            height={documentRef.current?.clientHeight || 1200}
            style={{
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentPreview; 